import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Hook to manage pagination, search query, and showArchived state in URL query string
 * @param {Object} options - Configuration options
 * @param {number} options.defaultPage - Default page number (default: 1)
 * @param {string} options.defaultSearch - Default search term (default: "")
 * @param {boolean} options.defaultShowArchived - Default show archived state (default: false)
 * @param {number} options.debounceMs - Debounce delay for search (default: 300)
 * @returns {Object} Hook state and methods
 */
export const usePaginationSearch = ({
    defaultPage = 1,
    defaultSearch = "",
    defaultShowArchived = false,
    debounceMs = 300,
} = {}) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const isNavigatingBack = useRef(false);
    const lastUrlParams = useRef(null);

    // Get initial values from URL params or use defaults
    const getInitialPage = () => {
        const page = searchParams.get("page");
        return page ? parseInt(page, 10) : defaultPage;
    };

    // Get current page from URL or preserve existing state
    const getCurrentPageFromUrl = () => {
        const page = searchParams.get("page");
        return page ? parseInt(page, 10) : null;
    };

    const getInitialSearch = () => {
        return searchParams.get("search") || defaultSearch;
    };

    const getInitialShowArchived = () => {
        const archived = searchParams.get("showArchived");
        return archived ? archived === "true" : defaultShowArchived;
    };

    // State management
    const [currentPage, setCurrentPage] = useState(getInitialPage);
    const [searchTerm, setSearchTerm] = useState(getInitialSearch);
    const [debouncedSearchTerm, setDebouncedSearchTerm] =
        useState(getInitialSearch);
    const [showArchived, setShowArchived] = useState(getInitialShowArchived);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, debounceMs);

        return () => clearTimeout(timer);
    }, [searchTerm, debounceMs]);

    // Update URL when state changes
    const updateUrl = useCallback(
        (updates) => {
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);

                Object.entries(updates).forEach(([key, value]) => {
                    if (value === null || value === undefined || value === "") {
                        newParams.delete(key);
                    } else {
                        newParams.set(key, value.toString());
                    }
                });

                return newParams;
            });
        },
        [setSearchParams]
    );

    // Sync state -> URL in a single effect.
    // Splitting this across three effects causes back-to-back setSearchParams
    // calls within the same flush to race: each functional updater reads a
    // stale `prev`, so the second/third write can clobber the first
    // (page=1 was being added then immediately removed on first mount).
    useEffect(() => {
        updateUrl({
            page: currentPage,
            search: debouncedSearchTerm,
            showArchived: showArchived,
        });
    }, [currentPage, debouncedSearchTerm, showArchived, updateUrl]);

    // Listen to URL changes and sync state (for browser back/forward)
    useEffect(() => {
        const currentUrlParams = searchParams.toString();

        // Skip if this is the same URL params as last time (prevents infinite loops)
        if (lastUrlParams.current === currentUrlParams) {
            return;
        }

        lastUrlParams.current = currentUrlParams;

        const urlPage = getCurrentPageFromUrl();
        const urlSearch = getInitialSearch();
        const urlShowArchived = getInitialShowArchived();

        // Set flag to indicate we're navigating back from URL changes
        isNavigatingBack.current = true;

        // Only update if values are different from current state
        // For page, only update if there's actually a page parameter in the URL
        // This prevents resetting to page 1 when navigating back from a detail page
        if (urlPage !== null && urlPage !== currentPage) {
            setCurrentPage(urlPage);
        }

        // Only update search if it's different and not empty (to avoid resetting on navigation)
        if (urlSearch !== searchTerm) {
            setSearchTerm(urlSearch);
            setDebouncedSearchTerm(urlSearch);
        }

        // Reset the flag immediately after state updates
        isNavigatingBack.current = false;
    }, [searchParams]);

    // Methods to update state
    const updatePage = useCallback((page) => {
        setCurrentPage(page);
    }, []);

    const updateSearch = useCallback((search) => {
        setSearchTerm(search);
        setCurrentPage(1);
    }, []);

    const updateShowArchived = useCallback((archived) => {
        setShowArchived(archived);
        setCurrentPage(1);
    }, []);

    // Reset all filters
    const resetFilters = useCallback(() => {
        setSearchTerm(defaultSearch);
        setShowArchived(defaultShowArchived);
        setCurrentPage(defaultPage);
    }, [defaultSearch, defaultShowArchived, defaultPage]);

    // Clear search only
    const clearSearch = useCallback(() => {
        setSearchTerm(defaultSearch);
        setCurrentPage(1);
    }, [defaultSearch]);

    return {
        // State
        currentPage,
        searchTerm,
        debouncedSearchTerm,
        showArchived,

        // Methods
        updatePage,
        updateSearch,
        updateShowArchived,
        resetFilters,
        clearSearch,

        // Computed values
        hasActiveFilters:
            searchTerm !== defaultSearch ||
            showArchived !== defaultShowArchived,
        isSearching: searchTerm !== debouncedSearchTerm,
    };
};
